import { useMemo, useState } from 'react';
import { Bell, Check, ExternalLink, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { AlertSeverity, AlertStatus, AlertType, LocalAlert } from '@mytag/shared';
import { PageHeader } from '@/components/layout/AppShell';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Field,
  LoadingState,
  Select,
} from '@/components/ui/primitives';
import { ErrorState } from '@/components/common/ErrorState';
import { useAlerts, useUpdateAlert } from '@/hooks/queries';
import { formatDateTime } from '@/lib/format';

const TYPE_LABELS: Record<AlertType, string> = {
  stale_location: 'Position ancienne',
  low_battery: 'Batterie faible',
  geofence_enter: 'Entrée de zone',
  geofence_exit: 'Sortie de zone',
  refresh_failed: 'Échec de rafraîchissement',
  suspicious_location: 'Position suspecte',
  invalid_coordinates: 'Coordonnées invalides',
  provider_status_unavailable: 'Statut fournisseur non communiqué',
  legacy_event: 'Événement historique',
};

const STATUS_LABELS: Record<AlertStatus, string> = {
  new: 'Nouvelle',
  read: 'Lue',
  resolved: 'Résolue',
};

const SEVERITY_LABELS: Record<AlertSeverity, string> = {
  info: 'Information',
  warning: 'Avertissement',
  critical: 'Critique',
};

function diagnosticValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Non communiqué';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

function AlertCard({ alert, pending, onUpdate }: {
  alert: LocalAlert;
  pending: boolean;
  onUpdate: (status: 'read' | 'resolved') => void;
}): JSX.Element {
  return (
    <Card className={alert.status === 'resolved' ? 'opacity-70' : ''}>
      <CardContent className="space-y-3 pt-4 sm:pt-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap gap-2">
              <Badge tone={alert.severity === 'critical' ? 'danger' : alert.severity === 'warning' ? 'warning' : 'info'}>
                {SEVERITY_LABELS[alert.severity]}
              </Badge>
              <Badge tone={alert.status === 'resolved' ? 'success' : alert.status === 'new' ? 'danger' : 'neutral'}>
                {STATUS_LABELS[alert.status]}
              </Badge>
              <Badge tone={alert.dataMode === 'mock' ? 'warning' : 'neutral'}>
                {alert.dataMode === 'mock' ? 'Mock' : 'Réel'}
              </Badge>
            </div>
            <h2 className="mt-2 text-sm font-semibold">{alert.title}</h2>
            <p className="mt-1 text-sm">{alert.message}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {alert.deviceName ?? 'Tous les appareils'}
              {alert.deviceNum ? ` · ${alert.deviceNum}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {alert.status === 'new' ? (
              <Button
                size="sm"
                variant="outline"
                loading={pending}
                icon={<Check className="h-3.5 w-3.5" />}
                onClick={() => onUpdate('read')}
              >
                Marquer comme lue
              </Button>
            ) : null}
            {alert.status !== 'resolved' ? (
              <Button
                size="sm"
                variant="outline"
                loading={pending}
                icon={<ShieldCheck className="h-3.5 w-3.5" />}
                onClick={() => onUpdate('resolved')}
              >
                Marquer comme résolue
              </Button>
            ) : null}
            {alert.deviceNum ? (
              <Link to={`/devices/${alert.deviceNum}`}>
                <Button size="sm" variant="secondary" icon={<ExternalLink className="h-3.5 w-3.5" />}>
                  Ouvrir l’appareil
                </Button>
              </Link>
            ) : null}
          </div>
        </div>

        <details className="rounded-md border border-border bg-muted/30 p-3 text-xs">
          <summary className="cursor-pointer font-medium">Afficher la règle et la source</summary>
          <dl className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-2">
            <dt className="text-muted-foreground">Type</dt><dd>{TYPE_LABELS[alert.type]}</dd>
            <dt className="text-muted-foreground">Valeur observée</dt><dd className="break-all font-mono">{diagnosticValue(alert.observedValue)}</dd>
            <dt className="text-muted-foreground">Seuil</dt><dd className="break-all font-mono">{diagnosticValue(alert.threshold)}</dd>
            <dt className="text-muted-foreground">Source</dt><dd>{alert.source}</dd>
            <dt className="text-muted-foreground">Observation</dt><dd>{alert.observedAt ? formatDateTime(alert.observedAt) : 'Date de dernière position non communiquée'}</dd>
            <dt className="text-muted-foreground">Création</dt><dd>{formatDateTime(alert.createdAt)}</dd>
            <dt className="text-muted-foreground">Lecture</dt><dd>{formatDateTime(alert.readAt)}</dd>
            <dt className="text-muted-foreground">Résolution</dt><dd>{formatDateTime(alert.resolvedAt)}</dd>
          </dl>
        </details>
      </CardContent>
    </Card>
  );
}

export function AlertsPage(): JSX.Element {
  const query = useAlerts();
  const update = useUpdateAlert();
  const [type, setType] = useState<'all' | AlertType>('all');
  const [deviceNum, setDeviceNum] = useState('all');
  const [severity, setSeverity] = useState<'all' | AlertSeverity>('all');
  const [status, setStatus] = useState<'active' | 'all' | AlertStatus>('active');

  const allAlerts = useMemo(() => query.data?.alerts ?? [], [query.data]);
  const devices = useMemo(
    () => [...new Map(allAlerts.filter((alert) => alert.deviceNum).map((alert) => [alert.deviceNum!, alert.deviceName ?? alert.deviceNum!])).entries()],
    [allAlerts],
  );
  const filtered = allAlerts.filter((alert) =>
    (type === 'all' || alert.type === type) &&
    (deviceNum === 'all' || alert.deviceNum === deviceNum) &&
    (severity === 'all' || alert.severity === severity) &&
    (status === 'all' || (status === 'active' ? alert.status !== 'resolved' : alert.status === status)));
  const missingDates = query.data?.positionStates.filter((item) => !item.lastReliablePositionAt) ?? [];

  if (query.isLoading) return <LoadingState label="Chargement des alertes…" />;
  if (query.isError) return <ErrorState error={query.error} />;

  return (
    <>
      <PageHeader
        title="Alertes"
        description="Alertes locales séparées par mode, basées uniquement sur les observations fiables disponibles."
      />

      {missingDates.map((item) => (
        <Alert key={item.deviceNum} tone="info" title={item.deviceName}>
          Date de dernière position non communiquée. Aucune alerte « Plus de 24 h » n’est générée.
        </Alert>
      ))}

      <Card>
        <CardContent className="grid gap-3 pt-4 sm:grid-cols-2 lg:grid-cols-4 sm:pt-5">
          <Field label="Type">
            <Select value={type} onChange={(event) => setType(event.target.value as typeof type)}>
              <option value="all">Tous</option>
              {Object.entries(TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </Select>
          </Field>
          <Field label="Appareil">
            <Select value={deviceNum} onChange={(event) => setDeviceNum(event.target.value)}>
              <option value="all">Tous</option>
              {devices.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </Select>
          </Field>
          <Field label="Sévérité">
            <Select value={severity} onChange={(event) => setSeverity(event.target.value as typeof severity)}>
              <option value="all">Toutes</option>
              {Object.entries(SEVERITY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </Select>
          </Field>
          <Field label="Statut">
            <Select value={status} onChange={(event) => setStatus(event.target.value as typeof status)}>
              <option value="active">Actives</option>
              <option value="new">Nouvelles</option>
              <option value="read">Lues</option>
              <option value="resolved">Résolues</option>
              <option value="all">Toutes</option>
            </Select>
          </Field>
        </CardContent>
      </Card>

      {filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Bell className="h-5 w-5" />}
            title="Aucune alerte pour ces filtres"
            description="Les alertes résolues restent disponibles via le filtre Statut."
          />
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((alert) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              pending={update.isPending && update.variables?.id === alert.id}
              onUpdate={(nextStatus) => update.mutate({ id: alert.id, status: nextStatus })}
            />
          ))}
        </div>
      )}
    </>
  );
}
